import React, { useState } from "react";
import Headers from "../components/Headers";
import Footer from "../components/Footer";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { place_order } from "../store/reducers/orderReducer";
import { get_card_products } from "../store/reducers/cardReducer";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { toast } from "react-hot-toast";
import { getProxiedUrl } from "../utils/imageUtils";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  address: yup.string().required("Address is required"),
  email: yup
    .string()
    .email("Please enter a valid email")
    .required("Email is required"),
  phone: yup.string().required("Phone is required"),
  post: yup.string().required("Post is required"),
  province: yup.string().required("Province is required"),
  city: yup.string().required("City is required"),
  area: yup.string().required("Area is required"),
});

const Shipping = ({ sellerId, sellerName }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { banners } = useSelector((state) => state.home);
  const { userInfo } = useSelector((state) => state.auth);
  const { card_products, shipping_fee: cartShippingFee, buy_product_item, isLoading: cartLoading } = useSelector((state) => state.card);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const { products, shipping_fee, items, orderSummary: navOrderSummary } = location.state || {};
  const [res, setRes] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    email: "",
    phone: "",
    post: "",
    province: "",
    city: "",
    area: "",
  });
  const [notes, setNotes] = useState('');

  // Set document title and load saved shipping information
  React.useEffect(() => {
    document.title = "4POS eCommerce Customer";
    
    // Debug logging
    console.log('Shipping page - Navigation state:', location.state);
    console.log('Shipping page - Products from state:', products);
    console.log('Shipping page - Card products from Redux:', card_products);
    
    // Check if we have required data from navigation or cart
    const hasNavigationData = products && Array.isArray(products) && products.length > 0;
    const hasCartData = card_products && Array.isArray(card_products) && card_products.length > 0;
    
    console.log('Shipping page - Has navigation data:', hasNavigationData);
    console.log('Shipping page - Has cart data:', hasCartData);
    console.log('Shipping page - Products structure:', products);
    
    // If we have navigation data, we're good to go (coming from existing order)
    if (hasNavigationData) {
      console.log('Shipping page - Using navigation data from existing order');
      return; // Don't fetch cart data if we already have order data
    }
    
    if (!hasCartData) {
      // Try to fetch cart data if we don't have it
      if (sellerId) {
        if (userInfo && userInfo.id) {
          dispatch(get_card_products({ userId: userInfo.id, sellerId }));
        } else {
          // For guest users, use sessionId
          const sessionId = sessionStorage.getItem('guestSessionId');
          if (sessionId) {
            dispatch(get_card_products({ userId: null, sellerId, sessionId }));
          }
        }
      }
      
      // If still no data after a short delay, redirect to cart
      setTimeout(() => {
        if (!card_products || card_products.length === 0) {
          toast.error('No products found. Please add items to your cart first.');
          navigate(`/${sellerName}/card`);
        }
      }, 1000);
    }
    
    // Load saved shipping information from localStorage
    const savedShippingInfo = localStorage.getItem('shippingInfo');
    const savedNotes = localStorage.getItem('orderNotes');
    
    if (savedShippingInfo) {
      try {
        const parsedShippingInfo = JSON.parse(savedShippingInfo);
        setFormData(parsedShippingInfo);
        setRes(true); // Set form as completed if we have saved data
        toast.success('Saved shipping information loaded');
      } catch (error) {
        console.error('Error parsing saved shipping info:', error);
        // Clear invalid data
        localStorage.removeItem('shippingInfo');
      }
    }
    
    if (savedNotes) {
      setNotes(savedNotes);
    }
  }, [products, card_products, navigate, sellerName, dispatch, userInfo]);

  const responsiveBanner = {
    superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 1 },
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 1 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: formData, // Use saved form data as default values
  });

  // Prefill form from existing order (navigation state) or localStorage
  React.useEffect(() => {
    // Prefer navigation order summary's shippingInfo if available
    if (navOrderSummary && navOrderSummary.shippingInfo && Object.keys(navOrderSummary.shippingInfo).length > 0) {
      const info = navOrderSummary.shippingInfo;
      setFormData(info);
      setRes(true);
      reset(info);
      try { localStorage.setItem('shippingInfo', JSON.stringify(info)); } catch (_) {}
      if (typeof info.notes === 'string' && info.notes) {
        setNotes(info.notes);
        try { localStorage.setItem('orderNotes', info.notes); } catch (_) {}
      }
      return;
    }

    // Fallback to saved shipping info in localStorage
    try {
      const saved = localStorage.getItem('shippingInfo');
      if (saved) {
        const parsed = JSON.parse(saved);
        setFormData(parsed);
        setRes(true);
        reset(parsed);
      }
      const savedNotes = localStorage.getItem('orderNotes');
      if (savedNotes) setNotes(savedNotes);
    } catch (_) {}
  }, [navOrderSummary, reset]);

  const save = (data) => {
    setFormData(data);
    setRes(true);
    // Save shipping info to localStorage
    localStorage.setItem('shippingInfo', JSON.stringify(data));
    // Save notes to localStorage
    localStorage.setItem('orderNotes', notes);
    toast.success('Shipping information saved successfully!');
  };

  const clearSavedInfo = () => {
    // Clear localStorage
    localStorage.removeItem('shippingInfo');
    localStorage.removeItem('orderNotes');
    
    // Reset form data
    const emptyFormData = {
      name: "",
      address: "",
      email: "",
      phone: "",
      post: "",
      province: "",
      city: "",
      area: "",
    };
    setFormData(emptyFormData);
    setNotes('');
    setRes(false);
    
    // Reset the form
    reset(emptyFormData);
    
    toast.success('Saved shipping information cleared');
  };

  const placeOrder = async () => {
    try {
      setIsLoading(true);
      
      // If this is an existing order (coming from Pay Now), do NOT create a new order.
      // Navigate to payment with the existing order summary, but include any updated shipping info.
      if (navOrderSummary && navOrderSummary.orderNumber) {
        const updatedSummary = {
          ...navOrderSummary,
          shippingInfo: formData
        };
        navigate(`/${sellerName}/payment`, {
          replace: true,
          state: { orderSummary: updatedSummary }
        });
        return;
      }

      // Check if we have valid products data
      if (!currentProducts || !Array.isArray(currentProducts) || currentProducts.length === 0) {
        toast.error('No products found. Please add items to your cart first.');
        navigate(`/${sellerName}/card`);
        return;
      }
      
      // Get notes from localStorage
      const notes = localStorage.getItem('orderNotes') || '';
      
      // Prepare products with correct prices
      const productsWithPrices = currentProducts.map(seller => ({
        ...seller,
        products: seller.products.map(product => {
          // Calculate the correct price using calculateDiscountedPrice
          const finalPrice = calculateDiscountedPrice(product);
          return {
            ...product,
            originalPrice: finalPrice,
            priceDetails: {
              originalPrice: finalPrice,
              discount: product.productInfo.discount || 0,
              bulkPricing: product.productInfo.bulkPricing || [],
              addons: product.addons || []
            }
          };
        })
      }));

      const computedSubtotal = productsWithPrices.reduce((sellerTotal, seller) => {
        if (Array.isArray(seller.products)) {
          return sellerTotal + seller.products.reduce((total, product) => {
            return total + calculateDiscountedPrice(product);
          }, 0);
        }
        return sellerTotal;
      }, 0);

      // For guest checkout, pass null as userId and include customer info in shippingInfo
      const orderResponse = await dispatch(
        place_order({
          products: productsWithPrices,
          price: computedSubtotal,
          shipping_fee: currentShippingFee,
          shippingInfo: formData,
          userId: null, // Set to null for guest checkout
          items: currentItems,
          notes
        })
      ).unwrap();

      if (orderResponse?.orderNumber) {
        // Calculate order summary using the correct prices
        const finalSubtotal = productsWithPrices.reduce((sellerTotal, seller) => {
          if (Array.isArray(seller.products)) {
            return sellerTotal + seller.products.reduce((total, product) => {
              return total + product.originalPrice;
            }, 0);
          }
          return sellerTotal;
        }, 0);
        const finalShippingFee = Number(currentShippingFee || 0);
        const finalTotal = finalSubtotal + finalShippingFee;
        const currencySymbol = currentProducts[0]?.products[0]?.productInfo?.currency || "NA";
        const orderSummary = {
          subtotal: finalSubtotal,
          shippingFee: finalShippingFee,
          total: finalTotal,
          currencySymbol,
          items: currentItems,
          orderNumber: orderResponse.orderNumber,
          sellerId,
          products: productsWithPrices,
          shippingInfo: formData // Include shipping info for guest checkout
        };
        // console.log('Shipping -> Payment navigation state:', orderSummary);
        navigate(`/${sellerName}/payment`, { 
          replace: true,
          state: { orderSummary } 
        });
      }
    } catch (error) {
      console.error('Error placing order:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const [currencySymbol, setCurrencySymbol] = useState("NA");

  // Normalize products if coming from an existing order (orderSummary)
  const normalizedOrderProducts = React.useMemo(() => {
    if (!navOrderSummary || !Array.isArray(navOrderSummary.products) || navOrderSummary.products.length === 0) {
      return [];
    }

    const firstProduct = navOrderSummary.products[0] || {};
    const inferredShopName = firstProduct.shopName || firstProduct.sellerName || firstProduct.shop || 'Your Shop';

    const mappedProducts = navOrderSummary.products.map((p) => ({
      // Shape expected by CartItem / calculators
      productInfo: {
        name: p.name || p.productName || 'Product',
        images: Array.isArray(p.images) ? p.images : (p.image ? [p.image] : []),
        price: typeof p.price === 'number' ? p.price : (typeof p.unitPrice === 'number' ? p.unitPrice : 0),
        discount: typeof p.discount === 'number' ? p.discount : 0,
        bulkPricing: Array.isArray(p.bulkPricing) ? p.bulkPricing : [],
        packSize: p.packSize || 1,
        currency: p.currency || navOrderSummary.currencySymbol || 'NA'
      },
      quantity: p.quantity || 1,
      addons: Array.isArray(p.addons) ? p.addons : [],
      selectedImage: p.selectedImage || (Array.isArray(p.images) ? p.images[0] : ''),
      packSize: p.packSize || 1
    }));

    return [{
      shopName: inferredShopName,
      products: mappedProducts
    }];
  }, [navOrderSummary]);

  // Use navigation data (existing order) first, then explicit products, then cart
  const currentProducts = (normalizedOrderProducts.length > 0)
    ? normalizedOrderProducts
    : (products && Array.isArray(products) && products.length > 0
        ? products
        : (card_products && Array.isArray(card_products) && card_products.length > 0
            ? card_products
            : []));

  const currentShippingFee = (navOrderSummary?.shippingFee ?? shipping_fee ?? cartShippingFee ?? 0);
  const currentItems = (navOrderSummary?.items ?? items ?? buy_product_item ?? 0);

  // Update this useEffect to set the currency symbol based on the first product
  React.useEffect(() => {
    if (currentProducts && Array.isArray(currentProducts) && currentProducts.length > 0) {
      // Try first in-stock product; if not present, try any product
      const firstWithCurrency = currentProducts.flatMap(s => s?.products || []).find(p => p?.productInfo?.currency) || null;
      if (firstWithCurrency) {
        setCurrencySymbol(firstWithCurrency.productInfo.currency);
      }
    }
  }, [currentProducts]);

  const calculateDiscountedPrice = (product) => {
    if (!product?.productInfo) return 0;

    // If discountPrice is available and valid, use it instead of recalculating
    if (product.discountPrice && product.discountPrice > 0) {
      // The stored discountPrice already includes addons from the product details page
      // So we don't need to add addons again
      return product.discountPrice;
    }

    // Fallback to original calculation if no discountPrice is stored
    const {
        price = 0,
        discount = 0,
        bulkPricing = [],
        currency = "NA",
    } = product.productInfo;
    const quantity = product.quantity || 1;

    // Calculate addons price
    let addonsPrice = 0;
    if (Array.isArray(product.addons)) {
        addonsPrice = product.addons.reduce((total, addon) => {
            return total + (typeof addon.price === 'number' ? addon.price : 0);
        }, 0);
    }

    // Calculate total price
    let totalPrice;

    // Handle bulk pricing if available and valid
    if (Array.isArray(bulkPricing) && bulkPricing.length > 0) {
        const validBulkPricing = bulkPricing.filter(
            (bp) => bp?.quantity && bp?.price && !isNaN(bp.quantity) && !isNaN(bp.price)
        );

        if (validBulkPricing.length > 0) {
            // Mixed pack pricing logic: use largest available pack + remaining single items
            let remainingQuantity = quantity;
            let totalCost = 0;
            
            // Sort bulk pricing by quantity (largest first)
            const sortedBulkPricing = [...validBulkPricing].sort(
                (a, b) => (b.quantity || 0) - (a.quantity || 0)
            );
            
            // Use the largest pack that fits, but only once per iteration
            for (const bulkOption of sortedBulkPricing) {
                if (remainingQuantity >= bulkOption.quantity) {
                    const packsNeeded = Math.floor(remainingQuantity / bulkOption.quantity);
                    if (packsNeeded > 0) {
                        totalCost += bulkOption.price * packsNeeded;
                        remainingQuantity -= bulkOption.quantity * packsNeeded;
                        break; // Use only the largest applicable pack
                    }
                }
            }
            
            // Add remaining items at single item price
            if (remainingQuantity > 0) {
                totalCost += price * remainingQuantity;
            }
            
            totalCost += addonsPrice * quantity;
            return totalCost;
        }
    }

    // If no bulk pricing applies, handle regular price with discount
    if (discount > 0) {
        // Calculate discount on the base price only
        const baseDiscount = (price * discount) / 100;
        const discountedBasePrice = price - baseDiscount;
        
        // Calculate total with discounted base price and addons
        totalPrice = (discountedBasePrice * quantity) + (addonsPrice * quantity);
    } else {
        // Regular price without discount
        totalPrice = (price * quantity) + (addonsPrice * quantity);
    }

    return totalPrice;
  };

  const CartItem = ({ product }) => {
    if (!product?.productInfo) return null;

    const currency = product.productInfo.currency || "NA";
    const discountedPrice = calculateDiscountedPrice(product);
    const originalPrice = (product.productInfo.price || 0) * (product.quantity || 1);

    return (
        <div className="w-full flex flex-wrap mb-4 p-4 bg-white rounded-lg shadow-sm">
            <div className="flex sm:w-full gap-2 w-7/12">
                <div className="flex gap-2 justify-start items-center">
                    <img
                        className="w-[80px] h-[80px] object-cover rounded"
                        src={getProxiedUrl(product.selectedImage || product.productInfo.images?.[0] || "")}
                        alt={product.productInfo.name || "Product image"}
                    />
                    <div className="pr-4 text-slate-600">
                        <h2 className="text-md font-semibold">
                            {product.productInfo.name || "Unnamed Product"}
                        </h2>
                        <span className="text-sm">
                            Pack Size: {product.packSize || product.productInfo.packSize || "N/A"}
                        </span>
                        {product.addons && product.addons.length > 0 && (
                            <div className="mt-2">
                                <p className="text-sm font-medium text-gray-700">Addons:</p>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {product.addons.map((addon, index) => (
                                        <span
                                            key={index}
                                            className="text-xs bg-blue-100 text-blue-800 rounded-full px-2 py-1"
                                        >
                                            {addon.text || ""} ({currency}
                                            {(addon.price || 0).toFixed(2)})
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {product.productInfo.bulkPricing && product.productInfo.bulkPricing.length > 0 && (
                            <div className="mt-2">
                                <p className="text-sm font-medium text-gray-700">Bulk Pricing:</p>
                                <div className="flex flex-col gap-1 mt-1">
                                    {product.productInfo.bulkPricing
                                        .filter(bp => bp?.quantity && bp?.price)
                                        .sort((a, b) => (a.quantity || 0) - (b.quantity || 0))
                                        .map((bp, index) => (
                                            <div key={index} className="text-xs text-gray-600">
                                                {bp.quantity}+ items: {currency}
                                                {(bp.price || 0).toFixed(2)} each
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="flex justify-end w-5/12 sm:w-full sm:mt-3">
                <div className="pl-4 sm:pl-0 text-right">
                    <h2 className="text-lg font-bold text-green-600">
                        {currency}
                        {Number(discountedPrice).toFixed(2)}
                    </h2>
                    {discountedPrice < originalPrice && (
                        <p className="text-sm text-gray-500 line-through">
                            {currency}
                            {Number(originalPrice).toFixed(2)}
                        </p>
                    )}
                    <p className="text-sm text-gray-600">
                        Quantity: {product.quantity || 1}
                    </p>
                </div>
            </div>
        </div>
    );
  };

  return (
    <div>
      <Headers sellerId={sellerId} sellerName={sellerName} />

      <section className="h-[220px] mt-6 bg-cover bg-no-repeat relative bg-left">
        <Carousel
          autoPlay={true}
          infinite={true}
          arrows={false}
          showDots={false}
          responsive={responsiveBanner}
          className="h-full"
        >
          {banners &&
            banners.length > 0 &&
            banners.map((b, i) => (
              <div key={i} className="h-full w-full">
                <img
                  className="w-full h-full object-cover"
                  src={b.banner}
                  alt=""
                />
              </div>
            ))}
        </Carousel>
        <div className="absolute left-0 top-0 w-full h-full bg-[#2422228a]">
          <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
            <div className="flex flex-col justify-center gap-1 items-center h-full w-full text-white">
              <div className="flex justify-center items-center gap-2 text-2xl w-full">
                <Link to={`/${sellerName}`}>Home</Link>
                <span className="pt-2">
                  <MdOutlineKeyboardArrowRight />
                </span>
                <span>Place Order</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-[#eeeeee]">
        <div className="w-[85%] lg:w-[90%] md:w-[90%] sm:w-[90] mx-auto py-16">
          
          {/* Checkout Progress Indicator */}
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Checkout Progress</h2>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    ✓
                  </div>
                  <span className="ml-3 font-medium text-green-600">Shopping Cart</span>
                </div>
                <div className="flex-1 h-1 bg-gray-200 mx-4">
                  <div className="h-1 bg-green-500 w-full"></div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <span className="ml-3 font-medium text-orange-600">Shipping Info</span>
                </div>
                <div className="flex-1 h-1 bg-gray-200 mx-4">
                  <div className="h-1 bg-orange-500 w-1/3"></div>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <span className="ml-3 font-medium text-gray-500">Payment</span>
                </div>
              </div>
            </div>
          </div>

          {/* Existing Order Notice */}
          {products && products.length > 0 && products[0]?.orderNumber && (
            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-blue-800">Completing Payment for Existing Order</h3>
                    <p className="text-sm text-blue-700 mt-1">
                      You're completing payment for order #{products[0].orderNumber}. You can review and update your shipping information below.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="w-full flex flex-wrap">
            <div className="w-[67%] md-lg:w-full">
              <div className="flex flex-col gap-3">
                <div className="bg-white p-6 shadow-sm rounded-md">
                  {!res && (
                    <>
                      <h2 className="text-slate-600 font-bold pb-3">
                        Shipping Information
                      </h2>
                      <form onSubmit={handleSubmit(save)}>
                        <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <label htmlFor="name">Name</label>
                            <input
                              {...register("name")}
                              type="text"
                              className="w-full px-3 py-2 border border-slate-200 outline-none focus:border-indigo-500 rounded-md"
                              name="name"
                              placeholder="name"
                              id="name"
                            />
                            {errors.name && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.name.message}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <label htmlFor="address">Address</label>
                            <input
                              {...register("address")}
                              type="text"
                              className="w-full px-3 py-2 border border-slate-200 outline-none focus:border-indigo-500 rounded-md"
                              name="address"
                              placeholder="House no / building / street / area"
                              id="address"
                            />
                            {errors.address && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.address.message}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 mb-2 w-full">
                          <label htmlFor="email">Email</label>
                          <input
                            {...register("email")}
                            type="email"
                            className="w-full px-3 py-2 border border-slate-200 outline-none focus:border-indigo-500 rounded-md"
                            name="email"
                            placeholder="email@example.com"
                            id="email"
                          />
                          {errors.email && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.email.message}
                            </p>
                          )}
                        </div>{" "}
                        
                        <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <label htmlFor="phone">Phone</label>
                            <input
                              {...register("phone")}
                              type="text"
                              className="w-full px-3 py-2 border border-slate-200 outline-none focus:border-indigo-500 rounded-md"
                              name="phone"
                              placeholder="phone"
                              id="phone"
                            />
                            {errors.phone && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.phone.message}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <label htmlFor="post">Postal Code</label>
                            <input
                              {...register("post")}
                              type="text"
                              className="w-full px-3 py-2 border border-slate-200 outline-none focus:border-indigo-500 rounded-md"
                              name="post"
                              placeholder="postal code"
                              id="post"
                            />
                            {errors.post && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.post.message}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <label htmlFor="province">Province</label>
                            <input
                              {...register("province")}
                              type="text"
                              className="w-full px-3 py-2 border border-slate-200 outline-none focus:border-indigo-500 rounded-md"
                              name="province"
                              placeholder="province"
                              id="province"
                            />
                            {errors.province && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.province.message}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <label htmlFor="city">City</label>
                            <input
                              {...register("city")}
                              type="text"
                              className="w-full px-3 py-2 border border-slate-200 outline-none focus:border-indigo-500 rounded-md"
                              name="city"
                              placeholder="city"
                              id="city"
                            />
                            {errors.city && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.city.message}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <label htmlFor="area">Landmark</label>
                            <input
                              {...register("area")}
                              type="text"
                              className="w-full px-3 py-2 border border-slate-200 outline-none focus:border-indigo-500 rounded-md"
                              name="area"
                              placeholder="Landmark"
                              id="area"
                            />
                            {errors.area && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.area.message}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex md:flex-col md:gap-2 w-full gap-5 text-slate-600">
                          <div className="flex flex-col gap-1 mb-2 w-full">
                            <label htmlFor="notes">Order Notes (Optional)</label>
                            <textarea
                              id="notes"
                              name="notes"
                              rows="3"
                              className="w-full px-3 py-2 border border-slate-200 outline-none focus:border-indigo-500 rounded-md"
                              placeholder="Add any special instructions or notes for your order..."
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="mt-6">
                          <button
                            type="submit"
                            className="w-full py-4 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            Continue to Payment
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                  {res && (
                    <div className="flex flex-col gap-1">
                      <div className="flex justify-between items-center">
                        <h2 className="text-slate-600 font-semibold pb-2">
                          Deliver to {formData.name}
                        </h2>
                        <button
                          onClick={clearSavedInfo}
                          className="text-xs text-red-500 hover:text-red-700 underline"
                          title="Clear saved shipping information"
                        >
                          Clear Saved Info
                        </button>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm text-green-700 font-medium">
                            {products && products.length > 0 && products[0]?.orderNumber 
                              ? `Shipping information for existing order #${products[0].orderNumber}. Ready to proceed to payment.`
                              : 'Shipping information completed! Ready to proceed to payment.'
                            }
                          </span>
                        </div>
                      </div>
                      <p>
                        <span className="bg-blue-200 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded">
                          Home
                        </span>
                        <span className="text-slate-600 text-sm">
                          {formData.address} {formData.province} {formData.city}{" "}
                          {formData.area}
                        </span>
                        <span
                          onClick={() => setRes(false)}
                          className="text-indigo-500 cursor-pointer"
                        >
                          {" "}
                          change
                        </span>
                      </p>
                      <p className="text-slate-600 text-sm">
                        Email to {formData.email}
                      </p>
                      {notes && (
                        <p className="text-slate-600 text-sm">
                          <span className="font-medium">Notes:</span> {notes}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {currentProducts && Array.isArray(currentProducts) && currentProducts.length > 0 ? (
                  currentProducts.map((p, i) => (
                    <div
                      key={i}
                      className="flex bg-white p-4 flex-col gap-2 rounded-lg shadow-md"
                    >
                      <div className="flex justify-start items-center">
                        <h2 className="text-md text-slate-600 font-semibold">
                          {p?.shopName || 'Unknown Shop'}
                        </h2>
                      </div>
                      {p?.products && Array.isArray(p.products) && p.products.map((pt, j) => (
                        <CartItem key={j} product={pt} />
                      ))}
                    </div>
                  ))
                ) : cartLoading ? (
                  <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Loading Cart Items</h3>
                    <p className="text-gray-500">Please wait while we fetch your cart items...</p>
                  </div>
                ) : (
                  <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <div className="text-gray-500 mb-4">
                      <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Products Found</h3>
                    <p className="text-gray-500 mb-4">Please add items to your cart first.</p>
                    <button
                      onClick={() => navigate(`/${sellerName}/card`)}
                      className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      Go to Cart
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="w-[33%] md-lg:w-full">
              <div className="pl-3 md-lg:pl-0">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
                  {currentProducts && Array.isArray(currentProducts) && currentProducts.length > 0 ? (
                    currentProducts.map((seller, i) => {
                    const currency = seller?.products?.[0]?.productInfo?.currency || currencySymbol || "NA";
                    
                    // Calculate correct subtotal using calculateDiscountedPrice across all seller products
                    const subtotal = (Array.isArray(seller?.products) ? seller.products : []).reduce((total, product) => {
                      return total + calculateDiscountedPrice(product);
                    }, 0);
                    
                    const shipping = currentShippingFee || 0;
                    const total = subtotal + shipping;

                    return (
                      <div key={i}>
                        <div className="flex justify-between items-center mb-4">
                          <span className="text-gray-600">Subtotal ({currentItems} Items)</span>
                          <span className="font-semibold">
                            {currency}
                            {subtotal.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center mb-4">
                          <span className="text-gray-600">Shipping Fee</span>
                          <span className="font-semibold">
                            {currency}
                            {shipping.toFixed(2)}
                          </span>
                        </div>
                        <div className="border-t pt-4 mt-4">
                          <div className="flex justify-between items-center mb-6">
                            <span className="text-lg font-semibold">Total</span>
                            <span className="text-xl font-bold text-green-600">
                              {currency}
                              {total.toFixed(2)}
                            </span>
                          </div>
                          <button
                            onClick={placeOrder}
                            disabled={!res || isLoading}
                            className={`w-full py-4 rounded-lg flex items-center justify-center gap-2 text-white font-bold text-lg transition-all duration-200 shadow-lg ${
                                res && !isLoading 
                                    ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:shadow-xl" 
                                    : "bg-gray-400 cursor-not-allowed"
                            }`}
                          >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                    Processing Order...
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Complete Order
                                </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-gray-500 mb-4">
                        <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                      </div>
                      <p className="text-gray-500">No items to display</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer sellerId={sellerId} sellerName={sellerName} />
    </div>
  );
};

export default Shipping;